import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserPlus, Shield, Eye, PencilSimple } from '@phosphor-icons/react';
import { BACKEND_URL } from '../config';

const roleBadge = (role) => {
  const styles = {
    admin: 'bg-primary text-primary-foreground',
    manager: 'bg-accent text-accent-foreground',
    viewer: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-sm font-bold tracking-wide uppercase ${styles[role] || styles.viewer}`}>
      {role}
    </span>
  );
};

const TeamManagement = ({ currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '', role: 'viewer' });
  const [editingUser, setEditingUser] = useState(null);

  const isAdmin = currentUser?.role === 'admin';

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/users`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/team/add-member`, {
        email: newUser.email,
        password: newUser.password,
        name: newUser.name,
        role: newUser.role,
      }, { withCredentials: true });

      toast.success(`${newUser.name || newUser.email} added as ${newUser.role}`);
      setInviteOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'viewer' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/auth/users/${userId}/role`, {
        role: newRole,
      }, { withCredentials: true });
      toast.success('Role updated');
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    }
  };

  if (loading) return <div className="text-center py-8 text-muted-foreground">Loading team...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xl font-semibold tracking-tight">Team Members</div>
            <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">{users.length} MEMBERS</div>
          </div>
          {isAdmin && (
            <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="rounded-sm" data-testid="add-member-button">
                  <UserPlus size={16} weight="bold" className="mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="text-2xl tracking-tight">Add Team Member</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleInvite} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Name *</Label>
                    <Input
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      className="rounded-sm"
                      required
                      data-testid="member-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Email *</Label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="rounded-sm"
                      required
                      data-testid="member-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Password *</Label>
                    <Input
                      type="password"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="rounded-sm"
                      required
                      minLength={6}
                      data-testid="member-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="member-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin — Full access</SelectItem>
                        <SelectItem value="manager">Manager — Can edit data</SelectItem>
                        <SelectItem value="viewer">Viewer — Read only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full rounded-sm" data-testid="submit-member-button">Add Member</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="team-members-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Member</th>
                <th className="text-left py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Email</th>
                <th className="text-center py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Role</th>
                {isAdmin && (
                  <th className="text-center py-3 px-4 text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-border" data-testid={`team-member-${u.id}`}>
                  <td className="py-4 px-4">
                    <div className="font-medium">{u.name || '—'}</div>
                    {u.id === currentUser?.id && <span className="text-xs text-muted-foreground">(You)</span>}
                  </td>
                  <td className="py-4 px-4 text-muted-foreground">{u.email}</td>
                  <td className="py-4 px-4 text-center">
                    {editingUser === u.id ? (
                      <Select
                        defaultValue={u.role}
                        onValueChange={(v) => handleRoleChange(u.id, v)}
                      >
                        <SelectTrigger className="w-[140px] mx-auto rounded-sm" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      roleBadge(u.role)
                    )}
                  </td>
                  {isAdmin && (
                    <td className="py-4 px-4 text-center">
                      {u.id !== currentUser?.id && (
                        <button
                          onClick={() => setEditingUser(editingUser === u.id ? null : u.id)}
                          className="p-2 hover:bg-muted rounded-sm transition-colors"
                          title="Change role"
                          data-testid={`edit-role-${u.id}`}
                        >
                          <PencilSimple size={16} weight="regular" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border rounded-sm p-6">
        <div className="text-sm font-bold tracking-[0.15em] uppercase text-muted-foreground mb-4">Role Permissions</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={18} weight="bold" className="text-primary" />
              <span className="font-semibold">Admin</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Full dashboard access</li>
              <li>Add, edit, delete clients & sales</li>
              <li>Import & export data</li>
              <li>Manage team members & roles</li>
            </ul>
          </div>
          <div className="p-4 border border-border rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <PencilSimple size={18} weight="bold" className="text-accent" />
              <span className="font-semibold">Manager</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>Full dashboard access</li>
              <li>Add, edit, delete clients & sales</li>
              <li>Import & export data</li>
              <li>Cannot manage team</li>
            </ul>
          </div>
          <div className="p-4 border border-border rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <Eye size={18} weight="bold" className="text-muted-foreground" />
              <span className="font-semibold">Viewer</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>View dashboard & analytics</li>
              <li>Export reports</li>
              <li>Cannot modify any data</li>
              <li>Cannot manage team</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamManagement;
